export interface RetryOptions {
  attempts?: number
  delay?: number
  factor?: number
  maxDelay?: number
  retryIf?: (error: unknown) => boolean
  jitter?: number
  signal?: AbortSignal
}

const TRANSIENT_MESSAGES = [
  "load failed",
  "network connection was lost",
  "network request failed",
  "failed to fetch",
  "econnreset",
  "econnrefused",
  "etimedout",
  "socket hang up",
]

export function isTransientError(error: unknown): boolean {
  if (!error) return false
  const record = typeof error === "object" ? error as Record<string, unknown> : undefined
  const status = record?.status ?? record?.statusCode ?? (record?.response as Record<string, unknown> | undefined)?.status
  if (status === 408 || status === 425 || status === 429 || (typeof status === "number" && status >= 500)) return true
  const message = String(error instanceof Error ? error.message : error).toLowerCase()
  return TRANSIENT_MESSAGES.some((m) => message.includes(m)) || /\b(408|425|429|5\d\d)\b/.test(message)
}

export async function retry<T>(fn: () => Promise<T>, options: RetryOptions = {}): Promise<T> {
  const { attempts = 3, delay = 500, factor = 2, maxDelay = 10000, retryIf = isTransientError, jitter = 0.2 } = options

  let lastError: unknown
  for (let attempt = 0; attempt < attempts; attempt++) {
    options.signal?.throwIfAborted()
    try {
      return await fn()
    } catch (error) {
      lastError = error
      if (attempt === attempts - 1 || !retryIf(error)) throw error
      const base = Math.min(delay * Math.pow(factor, attempt), maxDelay)
      const spread = Math.max(0, base * jitter)
      const wait = Math.max(0, base - spread + Math.random() * spread * 2)
      await new Promise<void>((resolve, reject) => {
        const finish = () => {
          options.signal?.removeEventListener("abort", abort)
          resolve()
        }
        const timer = setTimeout(finish, wait)
        const abort = () => {
          clearTimeout(timer)
          reject(options.signal?.reason ?? new DOMException("Aborted", "AbortError"))
        }
        options.signal?.addEventListener("abort", abort, { once: true })
        if (options.signal?.aborted) abort()
      })
    }
  }
  throw lastError
}
